package com.smashvoter.api

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

/** Thrown when the server rejects a request because the user's vote quota is exhausted. */
class VoteLimitExceededException : Exception("You have used all your votes")

/**
 * HTTP client for the SmashVoter backend.
 * Base URL defaults to localhost:8080 for development.
 * Override by setting window.__API_BASE_URL in the page.
 */
class ApiClient {

    private val baseUrl: String = js("window.__API_BASE_URL || 'https://api.smashvoter.com'") as String

    private val httpClient = HttpClient(Js) {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
    }

    private fun HttpResponse.checkSuccess() {
        check(status.isSuccess()) { "HTTP ${status.value} ${status.description}" }
    }

    suspend fun requestCode(email: String) {
        httpClient.post("$baseUrl/auth/request-code") {
            contentType(ContentType.Application.Json)
            setBody(RequestCodeBody(email))
        }.checkSuccess()
    }

    suspend fun verifyCode(email: String, code: String): VerifyCodeResponse {
        val response = httpClient.post("$baseUrl/auth/verify-code") {
            contentType(ContentType.Application.Json)
            setBody(VerifyCodeBody(email, code))
        }
        response.checkSuccess()
        return response.body()
    }

    suspend fun logout(sessionId: String) {
        httpClient.post("$baseUrl/auth/logout") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        } // ignore logout errors intentionally
    }

    /**
     * Returns the email address that the server has on file for this session.
     * Use this to verify a stored session is still valid and belongs to the expected user.
     */
    suspend fun getMe(sessionId: String): MeResponse {
        val response = httpClient.get("$baseUrl/auth/me") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /** Fetches all pre-generated pairs for the session user (lazy-generated on first call). */
    suspend fun getPairs(sessionId: String): VotePairsResponse {
        val response = httpClient.get("$baseUrl/pairs") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        if (response.status == HttpStatusCode.TooManyRequests) throw VoteLimitExceededException()
        response.checkSuccess()
        return response.body()
    }

    suspend fun skip(votePairId: String, sessionId: String) {
        val response = httpClient.post("$baseUrl/skip") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(SkipBody(votePairId))
        }
        if (response.status == HttpStatusCode.TooManyRequests) throw VoteLimitExceededException()
        response.checkSuccess()
    }

    suspend fun vote(votePairId: String, winnerPersonId: String, loserPersonId: String, sessionId: String): VoteResponse {
        val response = httpClient.post("$baseUrl/vote") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(VoteBody(votePairId, winnerPersonId, loserPersonId))
        }
        if (response.status == HttpStatusCode.TooManyRequests) throw VoteLimitExceededException()
        response.checkSuccess()
        return response.body()
    }

    suspend fun getRanking(): List<RankedPerson> {
        val response = httpClient.get("$baseUrl/ranking")
        response.checkSuccess()
        return response.body()
    }

    /** Returns the onboarding status (and prior rating if already completed). */
    suspend fun getOnboardingStatus(sessionId: String): OnboardingStatusResponse {
        val response = httpClient.get("$baseUrl/onboarding/status") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    // ── Admin ──────────────────────────────────────────────────────────────

    /** Returns both source lists (PEOPLE + Onboarding) with their ELOs. */
    suspend fun getAdminLists(sessionId: String): AdminListsResponse {
        val response = httpClient.get("$baseUrl/admin/lists") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /** Returns the current working tournament roster. */
    suspend fun getRoster(sessionId: String): List<RosterEntry> {
        val response = httpClient.get("$baseUrl/admin/roster") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /** Adds a person to the working tournament roster. */
    suspend fun addToRoster(
        name: String,
        elo: Double,
        sourceType: String,
        sourceId: String?,
        sessionId: String
    ): RosterEntry {
        val response = httpClient.post("$baseUrl/admin/roster") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(AddToRosterBody(name, elo, sourceType, sourceId))
        }
        response.checkSuccess()
        return response.body()
    }

    /** Removes a person from the working tournament roster. */
    suspend fun removeFromRoster(id: String, sessionId: String) {
        val response = httpClient.delete("$baseUrl/admin/roster/$id") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
    }

    /** Starts the tournament by committing the roster to the final_roster table. */
    suspend fun startTournament(sessionId: String): TournamentStartedResponse {
        val response = httpClient.post("$baseUrl/admin/tournament/start") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /** Returns the current tournament configuration. */
    suspend fun getTournamentConfig(sessionId: String): TournamentConfig {
        val response = httpClient.get("$baseUrl/admin/tournament/config") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /** Saves the tournament configuration. */
    suspend fun saveTournamentConfig(config: TournamentConfig, sessionId: String): TournamentConfig {
        val response = httpClient.post("$baseUrl/admin/tournament/config") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(config)
        }
        response.checkSuccess()
        return response.body()
    }

    /** Returns all active name overrides. */
    suspend fun getNameOverrides(sessionId: String): List<NameOverride> {
        val response = httpClient.get("$baseUrl/admin/name-overrides") {
            header(HttpHeaders.Authorization, "Bearer $sessionId")
        }
        response.checkSuccess()
        return response.body()
    }

    /**
     * Upserts a name override for a player.
     * Pass an empty [overrideName] to remove the override.
     * Returns the saved override, or null if the override was removed.
     */
    suspend fun setNameOverride(
        sourceType: String,
        sourceId: String,
        overrideName: String,
        sessionId: String
    ): NameOverride? {
        val response = httpClient.post("$baseUrl/admin/name-overrides") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(SetNameOverrideBody(sourceType, sourceId, overrideName))
        }
        response.checkSuccess()
        return if (response.status == HttpStatusCode.NoContent) null else response.body()
    }

    /** Returns all completed tournament rounds with match results and per-level standings. */
    suspend fun getTrustData(): TrustSummaryResponse {
        val response = httpClient.get("$baseUrl/tournament/trust")
        response.checkSuccess()
        return response.body()
    }

    /** Submits the onboarding form and returns the calculated rating. */
    suspend fun submitOnboarding(
        nickname: String,
        gamingLevel: Int,
        ssbExperience: Int,
        skillLevel: Int,
        sessionId: String
    ): OnboardingResponse {
        val response = httpClient.post("$baseUrl/onboarding") {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $sessionId")
            setBody(OnboardingBody(nickname, gamingLevel, ssbExperience, skillLevel))
        }
        response.checkSuccess()
        return response.body()
    }
}
